/**
 * Taste der linken Funktionsleiste im Verkauf: Symbol oben, kurzer Text unten,
 * optional eine Zahl (z. B. offene Tische, zu bonierende Zeilen). Aktive Schalter
 * (Teilen, Rabatt, Kunde) sind im Markenton hinterlegt (POS V2, Komponente POS/Funktion).
 * Mit `menue` öffnet die Taste ein Menü („Mehr“) statt einen Schalter umzulegen.
 */
export type FunktionsIcon =
  | 'leeren'
  | 'bonieren'
  | 'teilen'
  | 'tische'
  | 'parken'
  | 'ausserhaus'
  | 'rabatt'
  | 'notiz'
  | 'gutschein'
  | 'training'
  | 'bediener'
  | 'pfand'
  | 'leergut'
  | 'schublade'
  | 'retoure'
  | 'kunde'
  | 'mehr'
  | 'online'
  | 'bar'
  | 'karte';

const PFADE: Record<FunktionsIcon, string> = {
  leeren: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  bonieren: 'M4 5h16v14H4zM8 9h8M8 13h5M16 17l4-4',
  teilen: 'M12 3v18M5 8h5M5 12h5M5 16h5M14 8h5M14 12h5M14 16h5',
  tische: 'M3 9h18M6 9v10M18 9v10M9 9v4M15 9v4M4 5h16',
  parken: 'M7 20V4h6a4 4 0 0 1 0 8H7',
  ausserhaus: 'M5 8h14l-1 12H6L5 8zM9 8V6a3 3 0 0 1 6 0v2',
  rabatt: 'M5 19L19 5M7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM17 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  notiz: 'M5 4h11l3 3v13H5zM8 10h8M8 14h6',
  gutschein: 'M3 8h18v4a2 2 0 0 0 0 4v4H3v-4a2 2 0 0 0 0-4V8zM9 8v12',
  training: 'M4 10l8-4 8 4-8 4-8-4zM8 12v4c0 1.5 2 2.5 4 2.5s4-1 4-2.5v-4M20 10v5',
  bediener: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  pfand: 'M10 2h4v4l2 3v12a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9l2-3V2zM8 14h8',
  leergut: 'M10 2h4v4l2 3v12a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9l2-3V2zM3 12l3 3 3-3',
  schublade: 'M3 10h18v9H3zM3 10l2-5h14l2 5M10 14h4',
  retoure: 'M9 14L4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3',
  kunde:
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21a7 7 0 0 1 14 0M16 3.5a4 4 0 0 1 0 7M22 21a7 7 0 0 0-4-6.3',
  mehr: 'M5 12h1M11.5 12h1M18 12h1',
  bar: 'M2 7h20v10H2zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM5 10v4M19 10v4',
  karte: 'M2 6h20v12H2zM2 10h20M6 15h4',
  online:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z',
};

/** Symbol einer Funktion – auch für Menüeinträge, Bon-Kopf und Zahltasten. */
export function FunktionsSymbol(props: { icon: FunktionsIcon; groesse?: number }) {
  const g = props.groesse ?? 24;
  return (
    <svg viewBox="0 0 24 24" width={g} height={g} aria-hidden="true" className="funktions-symbol">
      <path
        d={PFADE[props.icon]}
        fill="none"
        stroke="currentColor"
        strokeWidth={props.icon === 'mehr' ? 2.6 : 1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FunktionsTaste(props: {
  icon: FunktionsIcon;
  text: string;
  aktiv?: boolean;
  gefahr?: boolean;
  disabled?: boolean;
  title?: string | undefined;
  zahl?: number;
  /** Taste öffnet ein Menü: aria-haspopup/aria-expanded statt aria-pressed. */
  menue?: { offen: boolean };
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`funktion${props.aktiv === true ? ' aktiv' : ''}${props.gefahr === true ? ' gefahr' : ''}`}
      disabled={props.disabled}
      title={props.title}
      aria-pressed={props.menue === undefined ? props.aktiv : undefined}
      aria-haspopup={props.menue === undefined ? undefined : 'menu'}
      aria-expanded={props.menue?.offen}
      onClick={props.onClick}
    >
      <FunktionsSymbol icon={props.icon} />
      <span>{props.text}</span>
      {props.zahl !== undefined && props.zahl > 0 && <em className="zahl">{props.zahl}</em>}
    </button>
  );
}
